/**
 * O CANAL entre o `api.ts` (que descobre o bloqueio) e o aviso na tela.
 *
 * Existe pra o `api.ts` nao precisar importar React nem o componente: ele so avisa que
 * uma chamada voltou recusada por ponto, e quem desenha decide o que fazer.
 *
 * ⚠️ **Por que um evento, e nao o `window.location.href` que estava aqui antes**
 * (11/09/2026): o redirecionamento trocava a pagina INTEIRA da aba, e quem paga isso e'
 * quem nao bateu ponto nenhum — as OUTRAS abas. Medido no `access.log` de 9 dias uteis:
 * **137 telas sequestradas**, ~15 por dia, todas logo depois de alguem bater a SAIDA —
 * 23 vezes o Caixa, 23 o Atendimento, e 7 vezes um orcamento sendo digitado
 * (`/orcamentos/novo`), que se perdia inteiro. Relato do Ruan: *"o sistema abre varias
 * abas do ponto, sobrepondo as abas que ja estavam abertas"*.
 *
 * ⛔ **A trava NAO mudou** — quem recusa `/api/*` segue sendo o `exigirPontoDoDia` no
 * servidor, e a decisao dele de 31/08 esta intacta. O que mudou e' so o que a aba VIVA
 * faz ao levar o 403: ela ja carregou tudo, entao nao vira a tela de destrocos que o
 * desvio existia pra evitar. Quem CHEGA sem ponto continua caindo na tela de registro,
 * pelo `ExigirPontoDoDia` do App.tsx — la o boot e' de verdade e nao ha o que preservar.
 */

/** Nome do evento no `window`. Quem escuta e' o `AvisoPontoFechado`. */
export const EVENTO_PONTO_BLOQUEADO = 'fiodecobre:ponto-bloqueado';

/** Chamado pelo `api.ts` a cada resposta 403 com `erro: 'ponto_pendente'`. */
export function avisarPontoBloqueado(): void {
  window.dispatchEvent(new CustomEvent(EVENTO_PONTO_BLOQUEADO));
}

/**
 * PRA ONDE VOLTAR depois de bater a entrada.
 *
 * Pedido do Ruan (11/09/2026), depois de ver a equipe na tela de ponto: *"nao da' para
 * quando clicar em recarregar o sistema identificar que ela ja deu entrada e liberar a
 * tela, mantendo onde estava?"*. Ate aqui a tela de entrada mandava todo mundo pro
 * Dashboard, entao quem vinha do Caixa ou de um orcamento perdia o lugar.
 *
 * ⚠️ Mora no `sessionStorage` porque ele e' **por aba**: duas abas em telas diferentes
 * voltam cada uma pra sua. `localStorage` faria as duas voltarem pro mesmo lugar.
 */
const DESTINO_KEY = 'fiodecobre.ponto.voltar_para';

/** Um caminho interno serve de volta? (nao vale outro site, nem a propria tela de ponto.) */
function destinoUtil(caminho: string): boolean {
  if (!caminho.startsWith('/') || caminho.startsWith('//')) return false;
  if (caminho.startsWith('/ponto/')) return false;
  if (caminho.startsWith('/login')) return false;
  return true;
}

/** Chamado ANTES de mandar alguem pra tela de ponto, pra saber de onde ele saiu. */
export function guardarDestinoDoPonto(caminho: string): void {
  try {
    if (destinoUtil(caminho)) sessionStorage.setItem(DESTINO_KEY, caminho);
  } catch {
    // Aba anonima / storage bloqueado: sem destino guardado a volta cai no Dashboard,
    // que e' o comportamento de antes — degradar aqui nao pode travar a tela.
  }
}

/**
 * Onde a pessoa estava antes de cair na tela de ponto.
 *
 * ⚠️ **O `document.referrer` e' o que salva a aba SEQUESTRADA pelo codigo antigo**: ela
 * chegou aqui por um `window.location.href` que nao guardou nada, mas o navegador carrega
 * o endereco anterior na navegacao — e' o mesmo campo que o `access.log` registra e que
 * mediu as 137 telas perdidas ([ponto-bloqueio: DA-715]). Sem ele, toda aba que ja estava
 * aberta hoje voltaria pro Dashboard.
 */
export function lerDestinoDoPonto(): string {
  try {
    const guardado = sessionStorage.getItem(DESTINO_KEY);
    if (guardado && destinoUtil(guardado)) return guardado;
  } catch {
    // segue pro referrer
  }
  try {
    if (document.referrer) {
      const url = new URL(document.referrer);
      if (url.origin === window.location.origin && destinoUtil(url.pathname)) {
        return url.pathname + url.search;
      }
    }
  } catch {
    // referrer ausente ou ilegivel — cai no Dashboard
  }
  return '/';
}

/** A volta ja aconteceu: nao repetir na proxima vez que a tela abrir. */
export function limparDestinoDoPonto(): void {
  try {
    sessionStorage.removeItem(DESTINO_KEY);
  } catch {
    // nada a fazer
  }
}
